import { CategorizedImage } from 'src/utils/hotelFilter';
import { Rate } from 'src/utils/hotelFilters';
import { CommissionWithBreakdownType } from 'src/utils/pricing.util';

/**
 * Youth-sports season planner — choosing the three rooms a parent sees.
 *
 * The whole design goal is less thinking: a parent opens the team hotel, sees
 * three rooms, and books one. So the three are chosen for them rather than
 * being the first three the supplier happened to return.
 *
 * The rules, from Daniel (2026-08-30):
 *  - three cards;
 *  - refundable rates are the default WHERE AVAILABLE;
 *  - one or two rooms that sleep several, at most one single-bed room;
 *  - a room with one bed plus a sofa bed or pullout counts as two beds.
 *
 * That last rule is why bed counting cannot just read the bed type: "1 King
 * Bed and Sofa Bed" is a family room, and a family of four booking it as a
 * "one bed" room is the mistake this is here to prevent.
 */

export interface PlannerRoom extends Rate {
   roomName?: string;
   freeCancellation?: boolean;
   remainingRooms?: number;
   // The same commission shape the pricing helpers take, so the per-night
   // figure here is always the one the traveler is shown.
   commission?: CommissionWithBreakdownType;
   /** Identifies the rate; `blockId` rotates between search sessions, `roomId` does not. */
   roomId?: string | null;
   blockId?: string | null;
   /** The physical room this rate is for — what groups rate variants together. */
   parentRoomId?: string | number | null;
   /** One entry per night: what turns a nightly rate into a stay total. */
   dailyRate?: unknown[];
   totalPayment?: { tax?: number };
   /** Added client-side from the hotel's static record — the rates call has neither. */
   images?: unknown[];
   categorizedImages?: CategorizedImage[];
   /** The other rate plans for the same physical room (meal / cancellation choices). */
   options?: PlannerRoom[];
}

/** Sofa beds, pullouts and murphy beds sleep people, so they count. */
const EXTRA_BED_PATTERN = /(sofa|pull[- ]?out|pullout|sleeper|futon|murphy|day ?bed|trundle|rollaway)/i;

/** Any word that means "there is a bed here". */
const BED_WORD_PATTERN = /(king|queen|double|full|twin|single|bunk|bed)/i;

/**
 * "2 Queen Beds", "Two Doubles", "3 twin beds", "2 Queens".
 *
 * The bed word is what carries the count, and the literal word "bed" is
 * optional — suppliers write "2 Queens" as often as "2 Queen Beds".
 */
const BED_QUANTITY_PATTERN = /\b(\d+|two|three|four)\s+(?:[a-z]+\s+)?(king|queen|double|full|twin|single|bunk|bed)s?\b/gi;

const WORD_NUMBERS: Record<string, number> = { two: 2, three: 3, four: 4 };

/**
 * Sleeping surfaces named in one phrase.
 *
 * A phrase with no quantity but a bed word in it is one bed: "King Room" is a
 * bed, and reading it as zero is what made "King Room with Sofa Bed" come back
 * as a single-bed room.
 */
function countInPhrase(text: string): number {
   let total = 0;
   const quantities = new RegExp(BED_QUANTITY_PATTERN);
   let match: RegExpExecArray | null;
   while ((match = quantities.exec(text)) !== null) {
      const raw = match[1].toLowerCase();
      // `Number(raw) ?? 0` never falls back — Number() returns NaN, not null,
      // so a non-numeric match would have made the whole total NaN. eslint's
      // no-constant-binary-expression caught it; the fallback is real now.
      const parsed = WORD_NUMBERS[raw] ?? Number.parseInt(raw, 10);
      total += Number.isFinite(parsed) ? parsed : 0;
   }
   if (total > 0) return total;
   return BED_WORD_PATTERN.test(text) || EXTRA_BED_PATTERN.test(text) ? 1 : 0;
}

/**
 * How many sleeping surfaces a room offers, from whatever the supplier gave us:
 * the structured `beddings` / `bedType` fields first, then the room name.
 *
 * Returns null when there is nothing to read — the caller treats that as
 * "unknown" rather than as "one bed", because guessing low would push a family
 * into a room that cannot hold them.
 */
/**
 * How many sleeping surfaces a room offers, from whatever the supplier gave us:
 * the structured `beddings` / `bedType` fields first, then the room name.
 *
 * Returns null when there is nothing to read — the caller treats that as
 * "unknown" rather than as "one bed", because guessing low would push a family
 * into a room that cannot hold them.
 */
export function countBeds(room: PlannerRoom | null | undefined): number | null {
   if (!room) return null;

   const structured = room.beddings ?? room.bedType ?? undefined;
   const textParts = [
      Array.isArray(room.beddings) ? room.beddings.join(' and ') : room.beddings,
      room.bedType,
      room.roomName,
   ]
      .filter(Boolean)
      .map(String);

   if (!textParts.length) return null;

   const text = textParts.join(' and ');
   if (!text.trim()) return null;

   // Extract raw phrase segments from structured bedding fields
   const rawPhrases: string[] = [];
   if (Array.isArray(structured)) {
      structured.forEach(item => {
         if (item) String(item).split(/;|\+|&|and|,/i).forEach(p => {
            const trimmed = p.trim();
            if (trimmed) rawPhrases.push(trimmed);
         });
      });
   } else if (typeof structured === 'string' && structured.trim()) {
      structured.split(/;|\+|&|and|,/i).forEach(p => {
         const trimmed = p.trim();
         if (trimmed) rawPhrases.push(trimmed);
      });
   }

   let count = 0;
   if (rawPhrases.length > 0) {
      count = rawPhrases.reduce((sum, phrase) => sum + countInPhrase(phrase), 0);
   }

   // Fallback to text parsing if structured phrases yielded no beds
   if (count === 0) {
      count = countInPhrase(text);
   }

   // Sofa beds or pullouts in text add extra capacity if not already part of structured count
   const extraInText = EXTRA_BED_PATTERN.test(text);
   const extraInPhrases = rawPhrases.some(p => EXTRA_BED_PATTERN.test(p));
   if (extraInText && !extraInPhrases && count > 0) {
      const countText = countInPhrase(text);
      if (countText > count) {
         count = countText;
      } else {
         count += 1;
      }
   }

   // Fallback for Family, Triple, Quad room names when no explicit bed counts found
   if (count === 0) {
      const lower = text.toLowerCase();
      if (/\b(quad|quadruple)\b/i.test(lower)) count = 4;
      else if (/\b(triple)\b/i.test(lower)) count = 3;
      else if (/\b(family)\b/i.test(lower)) count = 2;
   }

   return count > 0 ? count : null;
}

/** A room a family can share: two or more sleeping surfaces. */
export const sleepsSeveral = (room: PlannerRoom): boolean => (countBeds(room) ?? 0) >= 2;

/**
 * Whether a PROPERTY should survive the search page's bed filter (NPT-1734).
 *
 * Deliberately not asked of the rates on the results page. That list carries exactly one
 * rate per hotel — BH caps ratesPerProperty at 1 for the 31-100 HID batches the search
 * sends — so judging the property by it hides any hotel whose cheapest room happens to be
 * a king. `bedTypes` is the property's static room-type catalogue, which answers the
 * question the traveler is actually asking: does this hotel HAVE a room that sleeps us.
 *
 * Unknown fails OPEN. An empty or missing catalogue means the supplier told us nothing,
 * not that the hotel is king-only, and a filter that hides hotels on missing data quietly
 * shrinks the results page with no way for the traveler to tell. Showing one extra hotel
 * is the cheaper mistake.
 */
export function propertySleepsAtLeast(bedTypes: unknown, minBeds: number): boolean {
   if (!minBeds) return true;
   if (!Array.isArray(bedTypes) || bedTypes.length === 0) return true;

   const readable = bedTypes.map(bedType => countAlternatives(String(bedType ?? ''))).filter((n): n is number => n != null);
   if (readable.length === 0) return true;

   return readable.some(n => n >= minBeds);
}

/**
 * Beds in one catalogue entry, where "or" offers a CHOICE of layouts.
 *
 * Suppliers write a convertible room as "1 king bed or 2 twin beds": the room is made up one
 * way or the other, never both. countBeds sums what it sees — right for "and", wrong here, and
 * wrong in the direction that matters: "1 king bed or 1 sofa bed" sums to 2 and would pass a
 * 2-bed filter on a room with one sleeping surface whichever way it is made up.
 *
 * So each alternative is counted on its own and the best one wins. Kept here rather than pushed
 * into countBeds because that function also feeds the planner's room cards, and this is a
 * catalogue-string concern.
 */
function countAlternatives(bedType: string): number | null {
   const counts = bedType
      .split(/\bor\b/i)
      .map(alternative => countBeds({ bedType: alternative.trim() }))
      .filter((n): n is number => n != null);

   return counts.length ? Math.max(...counts) : null;
}

const nightly = (room: PlannerRoom): number => {
   const value = Number(room?.commission?.marginBreakdown?.ourNightlyRate ?? room?.commission?.nightlyRate ?? 0);
   return Number.isFinite(value) && value > 0 ? value : Number.POSITIVE_INFINITY;
};

/**
 * Prefer the refundable version of the same room.
 *
 * Suppliers list the same room twice — once non-refundable and cheaper, once
 * refundable. Showing both wastes one of only three cards on a duplicate, and
 * a youth-sports trip is exactly the booking most likely to be cancelled.
 */
function preferRefundable(rooms: PlannerRoom[]): PlannerRoom[] {
   const byName = new Map<string, PlannerRoom>();
   for (const room of rooms) {
      const key = `${(room.roomName ?? '').toLowerCase().trim()}|${countBeds(room) ?? '?'}`;
      const held = byName.get(key);
      if (!held) {
         byName.set(key, room);
         continue;
      }
      const heldRefundable = held.freeCancellation === true;
      const roomRefundable = room.freeCancellation === true;
      if (roomRefundable && !heldRefundable) byName.set(key, room);
      else if (roomRefundable === heldRefundable && nightly(room) < nightly(held)) byName.set(key, room);
   }
   return Array.from(byName.values());
}

export interface PlannerRoomSelection {
   rooms: PlannerRoom[];
   /** True when at least one card is a refundable rate. */
   hasRefundable: boolean;
}

/**
 * Pick the three rooms to show, per the rules above.
 *
 * Order within the three is cheapest-first among the multi-bed rooms, then the
 * single-bed ones — a parent booking for a family should land on a room that
 * fits them without reading. Highlight 2-bed options first; if no multi-bed rooms
 * are found, return the top 3 best available rooms.
 */
export function pickPlannerRooms(allRooms: PlannerRoom[] | null | undefined, limit = 3): PlannerRoomSelection {
   const rooms = preferRefundable((allRooms ?? []).filter(room => nightly(room) < Number.POSITIVE_INFINITY));
   if (!rooms.length) return { rooms: [], hasRefundable: false };

   const cheapestFirst = [...rooms].sort((a, b) => {
      // Refundable first where the price is comparable, so the default a parent
      // sees is the one they can cancel.
      const refundable = Number(b.freeCancellation === true) - Number(a.freeCancellation === true);
      if (refundable !== 0 && Math.abs(nightly(a) - nightly(b)) < 25) return refundable;
      return nightly(a) - nightly(b);
   });

   const multiBed = cheapestFirst.filter(sleepsSeveral);
   const singleBed = cheapestFirst.filter(room => !sleepsSeveral(room));

   const picked: PlannerRoom[] = [];
   
   // 1. Prioritize multi-bed rooms (2+ beds) up to limit
   const multiBedTarget = Math.min(multiBed.length, limit);
   picked.push(...multiBed.slice(0, multiBedTarget));

   // 2. If fewer than limit multi-bed rooms exist, top up with single-bed rooms
   if (picked.length < limit && singleBed.length) {
      for (const room of singleBed) {
         if (picked.length >= limit) break;
         if (!picked.includes(room)) picked.push(room);
      }
   }

   // 3. Top up with any remaining multi-bed rooms if any were skipped
   if (picked.length < limit) {
      for (const room of multiBed) {
         if (picked.length >= limit) break;
         if (!picked.includes(room)) picked.push(room);
      }
   }

   return { rooms: picked.slice(0, limit), hasRefundable: picked.some(room => room.freeCancellation === true) };
}

/** Room pictures arrive as bare URLs or as objects, and the CDN default is too small for a card. */
export function firstRoomImage(images: unknown[] | undefined): string {
   const first = images?.[0] as { url?: string; image_url?: string; picture_url?: string } | string | undefined;
   if (!first) return '';
   const url = typeof first === 'string' ? first : (first.url ?? first.image_url ?? first.picture_url ?? '');
   return url ? url.replace('{size}', 'x500').replace(/s=312x/gi, 's=640x400') : '';
}
